import React, { useEffect, useMemo, useState } from 'react';
import { UserPlus, Phone, Mail, BookOpen, PhoneCall, MessageSquare } from 'lucide-react';
import { useGroup } from '../context/GroupContext';

const AgentLogs = () => {
  const { scopeOwnerIds } = useGroup() || {};
  const OPINION_SCRIPT_NAME = 'Opinion Services test';
  const SURVEY_ANALYSIS_ENDPOINT = '/api/analysis-survey-generate';
  const [logs, setLogs] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeTab, setActiveTab] = useState('phone'); // phone | chatbot
  const [selectedScript, setSelectedScript] = useState(''); // '' means no script filter

  // Opinion Analysis state
  const [opinionModal, setOpinionModal] = useState({ open: false, logId: null, loading: false });
  const [opinionResults, setOpinionResults] = useState({}); // Cache results by logId
  const TOTAL_OPINION_QUESTIONS = 5;
  const isNotMentioned = (value) =>
    typeof value === 'string' && value.trim().toLowerCase() === 'not mentioned';
  const normalizeScriptId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (value && typeof value === 'object' && typeof value.$oid === 'string') {
      return value.$oid.trim();
    }
    const text = String(value).trim();
    return text === '[object Object]' ? '' : text;
  };

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
    const query = scopeOwnerIds?.length ? `?groupMemberIds=${scopeOwnerIds.join(',')}` : '';
    fetch(`${apiBase}/api/agentlogs${query}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setLogs(data.logs);
        } else {
          console.error('Failed to fetch logs');
        }
      })
      .catch(err => {
        console.error('Error fetching logs:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [scopeOwnerIds]);

  const scriptOptions = useMemo(() => {
    const names = logs
      .map((log) => (log.scriptName || log.script || '').trim())
      .filter((name) => name);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const isPhoneLog = (l) => {
    const callType = (l?.callType || '').toLowerCase();
    if (callType === 'chatbot') return false;
    if (callType === 'inbound' || callType === 'outbound') return true;

    const callDirection = (l?.callDirection || '').toLowerCase();
    if (callDirection === 'inbound' || callDirection === 'outbound') return true;

    // Fallbacks for older rows where callType may not exist
    const callSid = String(l?.callSid || '');
    const callId = String(l?.callId || '');
    if (callSid.startsWith('chatbot')) return false;
    return callSid.startsWith('CA') || callId.startsWith('CA') || callSid.startsWith('session_');
  };

  const isChatbotLog = (l) => {
    const callType = (l?.callType || '').toLowerCase();
    if (callType === 'inbound' || callType === 'outbound') return false;
    if (callType === 'chatbot') return true;

    const callSid = String(l?.callSid || '').toLowerCase();
    return callSid === 'chatbot' || callSid === 'chatbot-public' || callSid.startsWith('chatbot');
  };

  const filteredLogs = useMemo(() => {
    let nextLogs = logs;
    if (activeTab === 'phone') nextLogs = nextLogs.filter(isPhoneLog);
    if (activeTab === 'chatbot') nextLogs = nextLogs.filter(isChatbotLog);

    if (selectedScript) {
      nextLogs = nextLogs.filter((log) => (log.scriptName || log.script || '').trim() === selectedScript);
    }
    return nextLogs;
  }, [logs, activeTab, selectedScript]);


  const toggleTranscript = (id) => {
    setExpandedIds(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    setShowScrollTop(scrollTop > 300);
  };

  const scrollToTop = () => {
    const scrollContainer = document.querySelector('[data-scroll-container]');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const downloadRecording = async (log) => {
    try {
      const callId = log.callSid || log.callId;

      if (!callId) {
        alert('No Call ID found for this log entry.');
        return;
      }

      console.log('Downloading recording for Call ID:', callId); // Debug log
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const downloadUrl = `${apiBase}/api/recording/${callId}/download`;
      const token = localStorage.getItem('token');

      // Fetch blob with auth token
      const res = await fetch(downloadUrl, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!res.ok) throw new Error('Failed to download recording');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recording-${callId}-${new Date(log.createdAt).toISOString().split('T')[0]}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading recording:', error);
      alert('Failed to download recording. Please try again.');
    }
  };

  // Opinion Analysis function
  const runOpinionAnalysis = async (log) => {
    if (!log.transcript) {
      alert('No transcript available for analysis.');
      return;
    }

    // Check if we already have cached results
    if (opinionResults[log._id]) {
      setOpinionModal({ open: true, logId: log._id, loading: false });
      return;
    }

    setOpinionModal({ open: true, logId: log._id, loading: true });

    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const scriptId = normalizeScriptId(log.scriptId);

      if (!scriptId) {
        alert('Missing scriptId for this log. Please refresh and retry.');
        setOpinionModal({ open: false, logId: null, loading: false });
        return;
      }

      const response = await fetch(`${apiBase}${SURVEY_ANALYSIS_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          transcript: log.transcript,
          logId: log._id,
          scriptId
        })
      });

      const data = await response.json();

      if (data.success) {
        setOpinionResults(prev => ({ ...prev, [log._id]: data.analysis }));
        setOpinionModal({ open: true, logId: log._id, loading: false });
      } else {
        alert('Failed to analyze transcript: ' + (data.message || 'Unknown error'));
        setOpinionModal({ open: false, logId: null, loading: false });
      }
    } catch (error) {
      console.error('Error running opinion analysis:', error);
      alert('Failed to run opinion analysis. Please try again.');
      setOpinionModal({ open: false, logId: null, loading: false });
    }
  };

  const closeOpinionModal = () => {
    setOpinionModal({ open: false, logId: null, loading: false });
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return '#28a745';
      case 'medium': return '#ffc107';
      case 'low': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return '#2e7d32';
      case 'neutral': return '#6c757d';
      case 'negative': return '#c62828';
      default: return '#6c757d';
    }
  };

  const getCompletionRate = (analysis) => {
    if (analysis?.completionRate) return analysis.completionRate;
    const questions = analysis?.questions || [];
    if (!questions.length) return null;
    const uniqueQuestions = new Set(
      questions
        .filter((q) => !isNotMentioned(q.answer))
        .map((q) => q.questionNumber ?? q.question ?? q.answerCode ?? q.answer ?? 'Unknown')
    ).size;
    const answeredCount = Math.min(uniqueQuestions, TOTAL_OPINION_QUESTIONS);
    return `${answeredCount}/${TOTAL_OPINION_QUESTIONS} questions answered`;
  };

  // Move CRM entry to Contacts
  const moveToContacts = async (crmId) => {
    if (!crmId) {
      alert('No CRM entry found for this call log.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const response = await fetch(`${apiBase}/api/crm/${encodeURIComponent(crmId)}/move-to-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (data.success) {
        // Update the log's CRM entry to reflect it's been moved
        setLogs(prevLogs => 
          prevLogs.map(log => {
            if (log.crmEntry?.id === crmId) {
              return {
                ...log,
                crmEntry: {
                  ...log.crmEntry,
                  linkedContactId: data.contactId || 'local'
                }
              };
            }
            return log;
          })
        );
        alert('Successfully added to Contacts!');
      } else {
        alert(`Failed to add to Contacts: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error moving to contacts:', error);
      alert('Failed to add to Contacts. Please try again.');
    }
  };

  // Badge component helpers (Tailwind)
  const OutcomeBadge = ({ value }) => {
    const v = (value || '').toLowerCase();
    if (v === 'success') return <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-lg text-xs font-semibold">Success</span>;
    if (v === 'failure') return <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-lg text-xs font-semibold">Failure</span>;
    return <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg text-xs font-semibold">{value || 'Unknown'}</span>;
  };

  const ConversionBadge = ({ value }) => {
    const v = (value || '').toLowerCase();
    const map = { high: 'bg-cyan-100 text-cyan-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-red-100 text-red-800' };
    const c = map[v] || 'bg-gray-100 text-gray-700';
    return <span className={`${c} px-2 py-0.5 rounded-lg text-xs font-semibold`}>{value || 'n/a'}</span>;
  };

  const MoodBadge = ({ value }) => {
    const v = (value || '').toLowerCase();
    const map = { positive: { c: 'bg-emerald-100 text-emerald-800', label: 'Positive' }, neutral: { c: 'bg-gray-100 text-gray-700', label: 'Neutral' }, negative: { c: 'bg-red-100 text-red-800', label: 'Negative' } };
    const { c, label } = map[v] || { c: 'bg-gray-100 text-gray-700', label: value || '—' };
    return <span className={`${c} px-2 py-0.5 rounded-lg text-xs font-semibold`}>{label}</span>;
  };

  const TABS = [
    { key: 'phone', label: 'Phone', Icon: PhoneCall },
    { key: 'chatbot', label: 'Chatbot', Icon: MessageSquare },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-[#F5F7FA] relative">
      {/* Header */}
      <header className="shrink-0 px-4 md:px-6 py-4 bg-white/80 backdrop-blur-sm border-b border-gray-200/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#459EA3] to-[#27AAE1] flex items-center justify-center text-white shadow-md shadow-[#459EA3]/20">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">Agent Call Logs</h1>
            <p className="text-xs text-gray-500 hidden sm:block">View and filter call and chat logs</p>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-4 md:p-6">
        {/* Tabs + Script filter */}
        <div className="shrink-0 flex flex-wrap items-center gap-3 mb-4">
          <div className="flex gap-1 p-1 rounded-xl bg-white border border-gray-200/80 shadow-sm">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-[#459EA3] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <tab.Icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Script:</label>
            <select
              value={selectedScript}
              onChange={(e) => setSelectedScript(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-white min-w-[180px]"
            >
              <option value="">All Scripts</option>
              {scriptOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <div
          data-scroll-container
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1"
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <span className="text-sm font-medium">Loading logs…</span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <BookOpen className="w-12 h-12 mb-2 opacity-50" />
              <span className="text-sm font-medium">No logs found</span>
            </div>
          ) : (
            <div className="space-y-4 pb-6">
              {filteredLogs.map(log => (
                <div
                  key={log._id}
                  className="rounded-2xl bg-white border border-gray-200/80 shadow-sm p-4 md:p-5"
                >
                  {/* CRM Contact */}
                  {log.crmEntry?.contact && (log.crmEntry.contact.firstName || log.crmEntry.contact.lastName || log.crmEntry.contact.email || log.crmEntry.contact.phone) && (
                    <div className="rounded-xl bg-[#DBF4F3]/50 border border-[#459EA3]/30 p-3 md:p-4 mb-4">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <strong className="text-sm text-gray-700">Contact:</strong>
                        <span className="font-semibold text-gray-900">
                          {log.crmEntry.contact.fullName ||
                           [log.crmEntry.contact.firstName, log.crmEntry.contact.lastName].filter(Boolean).join(' ') || 'Unknown'}
                        </span>
                        {log.crmEntry.linkedContactId && (
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-lg text-xs font-semibold">In Contacts</span>
                        )}
                      </div>
                      <div className="flex gap-4 flex-wrap text-sm text-gray-600">
                        {log.crmEntry.contact.email && (
                          <span className="flex items-center gap-1"><Mail size={14} /> {log.crmEntry.contact.email}</span>
                        )}
                        {log.crmEntry.contact.phone && (
                          <span className="flex items-center gap-1"><Phone size={14} /> {log.crmEntry.contact.phone}</span>
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-gray-700 mb-1"><strong className="text-gray-900">Script:</strong> {log.scriptName}</p>
                  <p className="text-xs text-gray-500 mb-1 break-all"><strong>Call ID:</strong> {log.callId || log.callSid}</p>
                  <p className="text-xs text-gray-500 mb-1"><strong>Date:</strong> {new Date(log.createdAt).toLocaleString()}</p>
                  {log.recordingDuration && (
                    <p className="text-xs text-gray-500 mb-2"><strong>Duration:</strong> {log.recordingDuration} seconds</p>
                  )}

                  <div className="flex flex-wrap gap-2 items-center mb-2">
                    {(log.crmEntry?.outcome || log.outcome) && (
                      <span className="text-xs"><strong className="text-gray-600 mr-1">Outcome:</strong><OutcomeBadge value={log.crmEntry?.outcome || log.outcome} /></span>
                    )}
                    {(log.crmEntry?.mood || log.mood) && (
                      <span className="text-xs"><strong className="text-gray-600 mr-1">Mood:</strong><MoodBadge value={log.crmEntry?.mood || log.mood} /></span>
                    )}
                    {log.crmEntry?.potentialConversion && (
                      <span className="text-xs"><strong className="text-gray-600 mr-1">Conversion:</strong><ConversionBadge value={log.crmEntry.potentialConversion} /></span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-4"><strong className="text-gray-900">Summary:</strong> {log.crmEntry?.summary || log.summary || 'N/A'}</p>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => toggleTranscript(log._id)}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                        expandedIds[log._id]
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : 'bg-[#459EA3] hover:bg-[#3d8a8e] text-white'
                      }`}
                    >
                      {expandedIds[log._id] ? 'Hide Transcript' : 'Show Transcript'}
                    </button>
                    <button
                      onClick={() => downloadRecording(log)}
                      className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                    >
                      Download Recording
                    </button>
                    {(log.scriptName || log.script || '').trim() === OPINION_SCRIPT_NAME && (
                      <button
                        onClick={() => runOpinionAnalysis(log)}
                        disabled={!log.transcript}
                        className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-violet-600 hover:bg-violet-700 text-white"
                      >
                        Opinion Analysis
                      </button>
                    )}
                    {log.crmEntry?.id && (
                      <button
                        onClick={() => moveToContacts(log.crmEntry.id)}
                        disabled={!!log.crmEntry.linkedContactId}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#459EA3] hover:bg-[#3d8a8e] text-white"
                        title={log.crmEntry.linkedContactId ? 'Already in Contacts' : 'Add to Main CRM (Contacts)'}
                      >
                        <UserPlus size={16} /> {log.crmEntry.linkedContactId ? 'In Contacts' : 'Add to Contacts'}
                      </button>
                    )}
                  </div>

                  {expandedIds[log._id] && (
                    <pre className="mt-4 p-4 rounded-xl bg-[#F9F9F9] border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap overflow-auto max-h-96">
                      {log.transcript}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-[#459EA3] hover:bg-[#3d8a8e] text-white shadow-lg hover:shadow-xl transition-all z-[1000] flex items-center justify-center text-xl font-bold"
        >
          ↑
        </button>
      )}

      {/* Opinion Analysis Modal - modern */}
      {opinionModal.open && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2000] p-4"
          onClick={closeOpinionModal}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-[#459EA3] to-[#3d8a8e] text-white">
              <h2 className="text-lg font-bold">Opinion Analysis</h2>
              <button
                onClick={closeOpinionModal}
                className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-5">
              {opinionModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-[#459EA3] rounded-full animate-spin" />
                  <p className="mt-4 text-sm text-gray-500">Analyzing transcript with AI…</p>
                </div>
              ) : opinionResults[opinionModal.logId] ? (
                <>
                  {(opinionResults[opinionModal.logId].summary ||
                    opinionResults[opinionModal.logId].sentiment ||
                    opinionResults[opinionModal.logId].completionRate ||
                    opinionResults[opinionModal.logId].questions?.length) && (
                      <div className="rounded-xl bg-gray-50 border-l-4 border-[#459EA3] p-4 mb-5">
                        {opinionResults[opinionModal.logId].summary && (
                          <>
                            <strong className="text-[#3d8a8e] text-sm">Summary</strong>
                            <p className="mt-1 text-gray-800 text-sm">{opinionResults[opinionModal.logId].summary}</p>
                          </>
                        )}
                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
                          {opinionResults[opinionModal.logId].sentiment && (
                            <span><strong>Sentiment:</strong>{' '}
                              <span style={{ color: getSentimentColor(opinionResults[opinionModal.logId].sentiment), fontWeight: 600 }}>
                                {opinionResults[opinionModal.logId].sentiment}
                              </span>
                            </span>
                          )}
                          {getCompletionRate(opinionResults[opinionModal.logId]) && (
                            <span><strong>Completion:</strong> {getCompletionRate(opinionResults[opinionModal.logId])}</span>
                          )}
                        </div>
                      </div>
                    )}
                  <div className="space-y-4">
                    {opinionResults[opinionModal.logId].questions?.map((q, idx) => (
                      <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <span className="bg-[#459EA3] text-white px-2 py-0.5 rounded-lg text-xs font-bold">{q.questionNumber}</span>
                          <span
                            className="text-white px-2 py-0.5 rounded-lg text-xs capitalize"
                            style={{ backgroundColor: getConfidenceColor(q.confidence) }}
                          >
                            {q.confidence || 'N/A'} confidence
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm mb-2">{q.question}</p>
                        <div className={`rounded-lg p-3 text-sm font-semibold ${q.answerCode > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                          {q.answerCode > 0 && <span className="mr-1">✓</span>}
                          {q.answer}
                          {q.answerCode > 0 && <span className="ml-1 font-normal text-gray-600">(Option {q.answerCode})</span>}
                        </div>
                        {q.evidence && q.evidence !== 'Not mentioned in transcript' && (
                          <p className="mt-2 text-xs text-gray-600 italic border-t border-gray-100 pt-2">"{q.evidence}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 text-sm py-8">No analysis data available.</p>
              )}
            </div>
            <div className="shrink-0 px-5 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeOpinionModal}
                className="px-5 py-2.5 rounded-xl bg-[#459EA3] hover:bg-[#3d8a8e] text-white text-sm font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentLogs;
